import { useTranslation } from 'react-i18next';
import { CoverageRailShell, type CoverageRailBucket } from './CoverageRailShell';
import { CoverageBar, RailDefectBadge } from './CoverageRailBits';
import type { RoleAxisBoardVM, RoleAxisTab } from './useRoleAxisBoard';
import styles from './CoverageRail.module.css';

/**
 * EAA-1817: the **Theo vai trò** rail — one row per standard role, with how
 * many ready applications that role reaches.
 *
 * The row's primary line is the Vietnamese NAME with the key in mono beneath,
 * the same order the design corrected the tables to on 2026-08-24: an admin
 * scanning for "Thủ thư" cannot find it in a column of `librarian`.
 *
 * There is no code tile here. The app rail's tile is a three-letter app code
 * (`lms`, `ach`) — a short, stable badge; a role key is `teacher_lead`, which
 * does not survive being cut to three characters, and the full key is already
 * on the row's second line.
 */
export function RoleCoverageRail({ vm }: { vm: RoleAxisBoardVM }) {
    const { t } = useTranslation();

    const buckets: CoverageRailBucket<RoleAxisTab>[] = [
        {
            key: 'all',
            label: t('console.roleCoverage.roleRail.tabs.all'),
            count: vm.roleRows.length,
        },
        {
            key: 'needsAttention',
            label: t('console.roleCoverage.roleRail.tabs.needsAttention'),
            count: vm.needsAttentionCount,
        },
        {
            key: 'uncovered',
            label: t('console.roleCoverage.roleRail.tabs.uncovered'),
            count: vm.uncoveredCount,
        },
    ];

    const readyAppCount = vm.roleRows[0]?.readyAppCount ?? 0;
    const linkedPairs = vm.roleRows.reduce((total, row) => total + row.coveredCount, 0);

    return (
        <CoverageRailShell<RoleAxisTab>
            title={t('console.roleCoverage.roleRail.title', { count: vm.roleRows.length })}
            ariaLabel={t('console.roleCoverage.roleRail.ariaLabel')}
            searchPlaceholder={t('console.roleCoverage.roleRail.filterPlaceholder')}
            searchAriaLabel={t('console.roleCoverage.roleRail.filterAriaLabel')}
            search={vm.search}
            onSearchChange={vm.setSearch}
            bucketsAriaLabel={t('console.roleCoverage.roleRail.tabsAriaLabel')}
            buckets={buckets}
            activeBucket={vm.tab}
            onBucketChange={vm.setTab}
            selectedKey={vm.selectedRoleKey}
            onSelect={vm.selectRole}
            noMatchText={t('console.roleCoverage.roleRail.noMatch')}
            footer={t('console.roleCoverage.roleRail.footer', {
                roleCount: vm.roleRows.length,
                linked: linkedPairs,
                total: vm.roleRows.length * readyAppCount,
            })}
            rows={vm.visibleRows.map((row) => ({
                key: row.roleKey,
                tile: null,
                title: row.roleName,
                caption: (
                    <>
                        <code className={styles.railRowKey}>{row.roleKey}</code>
                        <CoverageBar covered={row.coveredCount} total={row.readyAppCount} />
                    </>
                ),
                trailing: (
                    <RailDefectBadge
                        defectCount={row.hasPaused ? 1 : 0}
                        covered={row.coveredCount}
                        total={row.readyAppCount}
                    />
                ),
                ariaLabel: t('console.roleCoverage.roleRail.rowAriaLabel', {
                    name: row.roleName,
                    roleKey: row.roleKey,
                    covered: row.coveredCount,
                    total: row.readyAppCount,
                }),
            }))}
        />
    );
}

export default RoleCoverageRail;
